import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Avatar,
  Select,
  MenuItem,
  Pagination,
  Snackbar,
  TextField,
  InputAdornment,
  Checkbox,
  Collapse
} from '@mui/material';
import {
  ArrowBack,
  People,
  History,
  Search,
  Close
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getAllUsers, updateUserRole } from '../services/api';

const ROLE_CONFIG = {
  superadmin: { label: 'Super Admin', color: '#dc2626', bg: '#fef2f2' },
  admin: { label: 'Admin', color: '#7c3aed', bg: '#f5f3ff' },
  sales: { label: 'Sales', color: '#059669', bg: '#ecfdf5' },
  customer_success: { label: 'Customer Success', color: '#2563eb', bg: '#eff6ff' },
  user: { label: 'User', color: '#6b7280', bg: '#f9fafb' }
};

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSuperadmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkRole, setBulkRole] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const itemsPerPage = 20;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await getAllUsers(currentPage, itemsPerPage, debouncedSearch);
        setUsers(response.data.data || []);
        setTotalPages(response.data.pagination.total_pages);
        setTotalItems(response.data.pagination.total);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.response?.data?.error || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentPage, debouncedSearch]);

  // Clear selection when page/search changes
  useEffect(() => {
    setSelectedUserIds([]);
  }, [currentPage, debouncedSearch]);

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingUserId(userId);
    try {
      await updateUserRole(userId, newRole);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setSnackbar({
        open: true,
        message: t('admin.roleUpdated', 'Role updated successfully'),
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating role:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('admin.roleUpdateFailed', 'Failed to update role'),
        severity: 'error'
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  // --- Selection logic ---
  const selectableUsers = users.filter(u => {
    const role = u.role || 'user';
    if (role === 'superadmin') return false;
    if (!isSuperadmin && role === 'admin') return false;
    return true;
  });

  const allSelectableSelected = selectableUsers.length > 0 &&
    selectableUsers.every(u => selectedUserIds.includes(u.id));

  const someSelected = selectedUserIds.length > 0;

  const handleToggleUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleToggleAll = () => {
    if (allSelectableSelected) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(selectableUsers.map(u => u.id));
    }
  };

  // --- Bulk role change ---
  const bulkAvailableRoles = isSuperadmin
    ? ['admin', 'sales', 'customer_success', 'user']
    : ['sales', 'customer_success', 'user'];

  const handleBulkApply = async () => {
    if (!bulkRole || selectedUserIds.length === 0) return;
    setBulkUpdating(true);

    let successCount = 0;
    let failCount = 0;

    for (const userId of selectedUserIds) {
      try {
        await updateUserRole(userId, bulkRole);
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: bulkRole } : u));
        successCount++;
      } catch (err) {
        console.error(`Failed to update role for ${userId}:`, err);
        failCount++;
      }
    }

    setBulkUpdating(false);
    setSelectedUserIds([]);
    setBulkRole('');

    if (failCount === 0) {
      setSnackbar({
        open: true,
        message: t('admin.bulkSuccess', 'Updated {{count}} users', { count: successCount }),
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: t('admin.bulkPartial', 'Updated {{success}}, failed {{fail}}', { success: successCount, fail: failCount }),
        severity: 'warning'
      });
    }
  };

  const getAvailableRoles = (currentRole) => {
    if (currentRole === 'superadmin') return [];
    if (isSuperadmin) return ['admin', 'sales', 'customer_success', 'user'];
    if (currentRole === 'admin') return [];
    return ['sales', 'customer_success', 'user'];
  };

  const getRoleChip = (role) => {
    const config = ROLE_CONFIG[role] || ROLE_CONFIG.user;
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          fontWeight: 600, fontSize: 12,
          color: config.color, bgcolor: config.bg,
          borderRadius: 1, height: 24
        }}
      />
    );
  };

  if (error && !users.length) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <AppBar position="static" elevation={0} sx={{
          bgcolor: '#fff', color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <Toolbar>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/')}
              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
              {t('common.backToCalculator', 'Back to Calculator')}
            </Button>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <style>
        {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {/* Header */}
      <AppBar position="static" elevation={0} sx={{
        bgcolor: '#fff', color: 'text.primary',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <Toolbar>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/')}
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
            {t('common.backToCalculator', 'Back to Calculator')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              {t('admin.usersManagement', 'Users Management')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            {t('admin.users', 'Users')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('admin.usersDescription', 'Manage user roles and view activity')}
          </Typography>
        </Box>

        {/* Search */}
        <Paper elevation={0} sx={{
          borderRadius: 3, border: 'none', p: 2, mb: 2.5,
          bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('admin.searchUsers', 'Search by email or name...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' }
              }
            }}
          />
        </Paper>

        {/* Bulk action bar */}
        <Collapse in={someSelected}>
          <Paper elevation={0} sx={{
            borderRadius: 3, border: '1px solid #00C853', p: 1.5, mb: 2.5,
            bgcolor: 'rgba(0, 200, 83, 0.04)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            display: 'flex', alignItems: 'center', gap: 2
          }}>
            <Chip
              label={t('admin.selectedCount', '{{count}} selected', { count: selectedUserIds.length })}
              size="small"
              sx={{ fontWeight: 600, bgcolor: '#00C853', color: 'white', height: 28 }}
            />
            <Select
              value={bulkRole}
              onChange={(e) => setBulkRole(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 180, height: 34, fontSize: 13, borderRadius: 1.5,
                '& .MuiSelect-select': { py: 0.5 },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' }
              }}
            >
              <MenuItem value="" disabled>
                {t('admin.selectRole', 'Select role...')}
              </MenuItem>
              {bulkAvailableRoles.map(role => (
                <MenuItem key={role} value={role}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: ROLE_CONFIG[role]?.color || '#6b7280'
                    }} />
                    {(ROLE_CONFIG[role] || ROLE_CONFIG.user).label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              size="small"
              disabled={!bulkRole || bulkUpdating}
              onClick={handleBulkApply}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: 1.5,
                bgcolor: '#00C853', '&:hover': { bgcolor: '#00A843' },
                height: 34, px: 2.5
              }}
            >
              {bulkUpdating ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                t('admin.applyRole', 'Apply')
              )}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Tooltip title={t('admin.clearSelection', 'Clear selection')}>
              <IconButton
                size="small"
                onClick={() => { setSelectedUserIds([]); setBulkRole(''); }}
                sx={{ color: 'text.secondary' }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Paper>
        </Collapse>

        {/* Results Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.showingUsers', 'Showing {{showing}} of {{total}} users', {
              showing: users.length,
              total: totalItems
            })}
          </Typography>
        </Box>

        {/* Users Table */}
        <Paper elevation={0} sx={{
          borderRadius: 3, border: 'none', overflow: 'hidden',
          bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          minHeight: 400, display: 'flex', flexDirection: 'column'
        }}>
          <TableContainer sx={{ flex: 1 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell padding="checkbox" sx={{ width: 48 }}>
                    <Checkbox
                      indeterminate={someSelected && !allSelectableSelected}
                      checked={allSelectableSelected}
                      onChange={handleToggleAll}
                      disabled={selectableUsers.length === 0}
                      sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#00C853' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 48 }}></TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('admin.name', 'Name')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('admin.email', 'Email')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('admin.role', 'Role')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 60 }}>
                    {t('admin.actions', 'Actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#00C853' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t('admin.loadingUsers', 'Loading users...')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                      <People sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                        {t('admin.noUsers', 'No users found')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin.adjustSearch', 'Try adjusting your search')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((userItem, index) => {
                    const availableRoles = getAvailableRoles(userItem.role || 'user');
                    const canChangeRole = availableRoles.length > 0;
                    const effectiveRole = userItem.role || 'user';
                    const isSelected = selectedUserIds.includes(userItem.id);

                    return (
                      <TableRow
                        key={userItem.id}
                        selected={isSelected}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(0, 200, 83, 0.02)' },
                          '&.Mui-selected': { bgcolor: 'rgba(0, 200, 83, 0.04)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(0, 200, 83, 0.06)' },
                          animation: `fadeInUp 0.4s ease-out ${index * 0.03}s both`
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleUser(userItem.id)}
                            disabled={!canChangeRole}
                            sx={{ '&.Mui-checked': { color: '#00C853' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Avatar
                            src={userItem.picture}
                            sx={{ width: 36, height: 36, bgcolor: '#e5e7eb', color: '#6b7280', fontSize: 14 }}
                          >
                            {userItem.nickname ? userItem.nickname.charAt(0).toUpperCase() : 'U'}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {userItem.nickname || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {userItem.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {canChangeRole ? (
                            <Select
                              value={effectiveRole}
                              onChange={(e) => handleRoleChange(userItem.id, e.target.value)}
                              size="small"
                              disabled={updatingUserId === userItem.id}
                              sx={{
                                minWidth: 160, height: 32, fontSize: 13, borderRadius: 1.5,
                                '& .MuiSelect-select': { py: 0.5 },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' }
                              }}
                            >
                              {!availableRoles.includes(effectiveRole) && (
                                <MenuItem value={effectiveRole} disabled>
                                  {(ROLE_CONFIG[effectiveRole] || ROLE_CONFIG.user).label}
                                </MenuItem>
                              )}
                              {availableRoles.map(role => (
                                <MenuItem key={role} value={role}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{
                                      width: 8, height: 8, borderRadius: '50%',
                                      bgcolor: ROLE_CONFIG[role]?.color || '#6b7280'
                                    }} />
                                    {(ROLE_CONFIG[role] || ROLE_CONFIG.user).label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          ) : (
                            getRoleChip(effectiveRole)
                          )}
                          {updatingUserId === userItem.id && (
                            <CircularProgress size={16} sx={{ ml: 1, color: '#00C853' }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t('admin.viewHistory', 'View history')}>
                            <IconButton
                              onClick={() => navigate(`/admin/users/${userItem.id}/history`)}
                              size="small"
                              sx={{
                                width: 32, height: 32, borderRadius: 1.5,
                                border: '1px solid #e5e7eb', bgcolor: 'white', color: '#6b7280',
                                '&:hover': { bgcolor: '#f3f4f6', borderColor: '#d1d5db', color: '#00C853' }
                              }}
                            >
                              <History sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#6b7280', fontWeight: 500, borderRadius: 2,
                    '&.Mui-selected': {
                      backgroundColor: '#00C853', color: 'white',
                      '&:hover': { backgroundColor: '#00A843' }
                    },
                    '&:hover': { backgroundColor: '#f3f4f6' }
                  }
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsersPage;
